'use client'
import Image from 'next/image'

import { clsx } from 'clsx'

import { FC } from 'react'

import { AnimationMoveIconComponent } from '@/app/shared/components/animation/move-icon'
import { AnimationShowBlockComponent } from '@/app/shared/components/animation/show-block'
import { ListComponent } from '@/app/shared/components/list'
import { getColorTextSite } from '@/app/shared/components/modals/modal/services'
import SignUpLessonComponent from '@/app/shared/components/modals/sign-up-lesson/sign-up-lesson.component'
import { IconBgPart } from '@/app/shared/icons'
import {
  IArrayListItem,
  IButton,
  IColorsSite,
  IContent,
  IDescription,
  IImg,
  ITitle,
} from '@/app/shared/interfaces/content.interface'
import { clearData } from '@/app/shared/services/data-formatter'
import { ITypeSite } from '@/app/shared/store/layout.store'
import useModal from '@/app/shared/store/modals.store'
import { ButtonComponent } from '@/app/shared/ui/button'
import { ContainerComponent } from '@/app/shared/ui/container'
import { TitleComponent } from '@/app/shared/ui/title'
import { t } from '@/core/lib/locale/i18n'

// interface
interface IWelcomePage {
  data: IWelcome
  typeSite: ITypeSite
  id: string
}

export interface IWelcome {
  arrayList: {
    items: IContent[]
    title: string | null
  }
  button: IButton
  colorsSite: IColorsSite
  image: IImg
  sticker: IImg[]
  title: ITitle
  description: IDescription
}

// component
const WelcomeComponent: FC<Readonly<IWelcomePage>> = ({ data, typeSite, id }) => {
  const arrayListItem = (clearData(data.arrayList.items) as IArrayListItem).arrayListItem.map(
    (el) => el.item,
  )
  const typeOnline = typeSite === 'online'
  const open = useModal((state) => state.open)

  const handleOpenModal = () => {
    open({
      component: SignUpLessonComponent,
      props: {
        title: data.button.text,
      },
    })
  }

  // return
  return (
    <ContainerComponent
      className={clsx(
        'grid w-full items-start gap-0 gap-y-[40px] pb-[73px] pt-[60px] mobile:gap-x-5 tablet:grid-cols-[322px_1fr] laptop:grid-cols-[450px_1fr] desktop:grid-cols-[570px_1fr] desktop:gap-y-[43px] large:grid-cols-[751px_1fr]',
        {
          'bg-secondary': typeOnline,
          'bg-success': !typeOnline,
        },
      )}
    >
      <div
        className={
          'extraMobile: grid gap-[30px] text-[24px] lg:col-start-2 lg:row-start-1 lg:gap-10'
        }
        id={id}
      >
        <TitleComponent
          typeTag={data.title.size}
          classSize={
            'text-[36px] extraMobile:text-[40px] lg:text-[50px] xl:text-[72px] 2xl:text-[82px]'
          }
          className={'font-extrabold leading-none text-primary'}
          animate
        >
          {data.title.title}
        </TitleComponent>
        <ListComponent
          data={arrayListItem}
          className={'gap[10px] text-[18px] lg:text-[22px] xl:gap-[23px]'}
          classIcon={'h-[30px] w-[30px] mr-[20px] text-content1-800'}
          colorIcon={'content1-800'}
        />
      </div>
      <AnimationShowBlockComponent
        className={
          'relative mx-auto h-[264px] w-full extraMobile:w-[400px] mobile:w-[440px] tablet:w-full laptop:col-start-1 laptop:row-span-3 laptop:mx-0 laptop:h-[399px] desktop:w-full large:h-[511px]'
        }
        initial={{
          opacity: 0,
          x: -40,
        }}
        whileInView={{
          opacity: 1,
          x: 0,
        }}
        viewport={{ once: true, amount: 0.5 }}
      >
        <div
          className={
            'absolute left-1/2 h-full w-[258px] -translate-x-1/2 laptop:w-[390px] desktop:w-[436px] large:w-[500px]'
          }
        >
          <Image
            src={data.image.image}
            alt={'welcome'}
            className={'h-full w-full object-cover'}
            width={600}
            height={611}
            priority
            quality={100}
          />
        </div>
        <AnimationMoveIconComponent
          className={
            'absolute -top-[20px] right-[17px] h-[66px] w-[62px] -rotate-[20deg] tablet:right-[7px] tablet:top-[30px] laptop:right-[4px] laptop:top-[45px] laptop:h-[83px] laptop:w-[78px] desktop:right-[28px] desktop:top-[42px] desktop:h-[94px] desktop:w-[89px] large:right-[72px] large:top-[45px] large:h-[112px] large:w-[105px]'
          }
          rotate={-20}
          valueMoving={10}
        >
          <Image
            src={data.sticker[1].image}
            alt={'sticker'}
            fill
            className={'object-contain'}
            sizes={'(max-width:1028px):71px, (max-width:1600px):78px, 88px'}
          />
        </AnimationMoveIconComponent>
        <AnimationMoveIconComponent
          className={
            'absolute -bottom-[33px] right-[19px] h-[67px] w-[52px] rotate-[20deg] tablet:-bottom-[48px] tablet:right-[36px] laptop:-bottom-[60px] laptop:right-[42px] laptop:h-[101px] laptop:w-[79px] desktop:-bottom-[65px] desktop:right-[54px] large:-bottom-[115px] large:right-[96px] large:h-[127px] large:w-[99px]'
          }
          rotate={20}
          valueMoving={55}
        >
          <Image
            src={data.sticker[0].image}
            alt={'sticker'}
            fill
            className={'object-contain'}
            sizes={'(max-width:1028px):59px, (max-width:1600px):70px, 87px'}
          />
        </AnimationMoveIconComponent>
        <AnimationMoveIconComponent
          className={
            'absolute left-[36px] top-[87px] z-[1] h-[56px] w-[71px] -rotate-[10deg] mobile:top-[95px] tablet:left-[2px] tablet:top-[142px] laptop:left-[15px] laptop:top-[123px] laptop:h-[75px] laptop:w-[95px] desktop:left-[7px] desktop:top-[160px] desktop:h-[84px] desktop:w-[106px] large:left-[69px] large:top-[173px] large:h-[93px] large:w-[118px]'
          }
          rotate={-10}
          valueMoving={135}
        >
          <Image
            src={data.sticker[2].image}
            alt={'sticker'}
            fill
            className={'object-contain'}
            sizes={'(max-width:1028px):77px, (max-width:1600px):98px, 126px'}
          />
        </AnimationMoveIconComponent>
        {data?.description?.description && (
          <div
            className={clsx(
              'absolute left-[6px] top-[19px] w-[131px] p-[7px_6px] text-[0.7rem] uppercase leading-tight text-content1-200 tablet:-top-[10px] tablet:left-[5px] laptop:left-[10px] laptop:top-[13px] laptop:w-[164px] laptop:text-[0.9rem] desktop:left-[15px] desktop:top-[5px] desktop:w-[112px] desktop:text-base desktop:font-semibold large:left-[24px] large:top-[105px] large:w-[207px]',
              { 'bg-content1-500': typeSite === 'online-espanol' },
              { 'bg-warning': typeSite !== 'online-espanol' },
            )}
          >
            {data?.description?.description}
            <IconBgPart
              className={clsx(
                'absolute -bottom-[12px] left-0 h-[12px] w-[17px] -scale-[1]',
                { 'text-content1-500': typeSite === 'online-espanol' },
                { 'text-warning': typeSite !== 'online-espanol' },
              )}
            />
          </div>
        )}
      </AnimationShowBlockComponent>
      <AnimationShowBlockComponent
        initial={{
          opacity: 0,
          x: 40,
        }}
        whileInView={{
          opacity: 1,
          x: 0,
        }}
        viewport={{ once: true, amount: 0.5 }}
      >
        <ButtonComponent
          color={'default'}
          size={'lg'}
          icon={clsx('ml-5 h-2xl w-2xl md:h-8 md:w-8', getColorTextSite(typeSite))}
          className={
            'mx-auto w-full tablet:col-span-2 tablet:row-start-2 tablet:w-[443px] laptop:col-start-2 laptop:mx-0 laptop:w-full'
          }
          onClick={handleOpenModal}
        >
          <span className={'font-semibold'}>{t.sign_up_text()}</span>
        </ButtonComponent>
      </AnimationShowBlockComponent>
    </ContainerComponent>
  )
}

export default WelcomeComponent
